import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bug, ImagePlus, Loader2, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  BugImageContentType,
  BugReport,
  BugSeverity,
  createBugAttachmentUpload,
  createBugReport,
  deleteBugReport,
  getBugReport,
  listBugReports,
  uploadBugAttachmentFile,
} from '../api/client';
import { useAuth } from '../context/AuthContext';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const PAGE_SIZE = 20;
const READ_URL_REFRESH_LEAD_MS = 30_000;
const SUPPORTED_IMAGE_TYPES = new Set<BugImageContentType>([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);

const severityClasses: Record<BugSeverity, string> = {
  low: 'bg-sky-100 text-sky-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

function errorDetail(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const response = (error as { response?: { data?: { detail?: unknown } } }).response;
  return typeof response?.data?.detail === 'string' ? response.data.detail : null;
}

function mergeNewest(current: BugReport[], incoming: BugReport[]): BugReport[] {
  const reports = new Map(current.map((report) => [report.id, report]));
  incoming.forEach((report) => reports.set(report.id, report));
  return Array.from(reports.values()).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export default function Bugs() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('medium');
  const [image, setImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reports, setReports] = useState<BugReport[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openingAttachmentId, setOpeningAttachmentId] = useState<string | null>(null);

  const canDelete = user?.role === 'owner' || user?.role === 'admin';
  const imagePreviewUrl = useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);

  useEffect(() => () => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
  }, [imagePreviewUrl]);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const page = await listBugReports(PAGE_SIZE);
      setReports(mergeNewest([], page.items));
      setNextCursor(page.next_cursor ?? null);
    } catch (error) {
      setListError(errorDetail(error) || t('bugs.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage, user?.tenant_id]);

  useEffect(() => {
    const attached = reports.filter((report) => report.attachment);
    if (attached.length === 0) return undefined;

    const refreshTimes = attached
      .map((report) => (
        new Date(report.attachment!.read_url_expires_at).getTime()
        - READ_URL_REFRESH_LEAD_MS
      ))
      .filter(Number.isFinite);
    if (refreshTimes.length === 0) return undefined;
    const nextRefreshAt = Math.min(...refreshTimes);
    const delay = Math.max(1_000, nextRefreshAt - Date.now());
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void Promise.all(attached.map((report) => getBugReport(report.id)))
        .then((refreshed) => {
          if (!cancelled) {
            setReports((current) => mergeNewest(current, refreshed));
          }
        })
        .catch(() => {
          // Opening an image also refreshes its URL, so a background failure is non-fatal.
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [reports]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!SUPPORTED_IMAGE_TYPES.has(selected.type as BugImageContentType)) {
      setImageError(t('bugs.validation.imageType'));
      event.target.value = '';
      return;
    }
    if (selected.size < 1) {
      setImageError(t('bugs.validation.imageEmpty'));
      event.target.value = '';
      return;
    }
    if (selected.size > MAX_IMAGE_BYTES) {
      setImageError(t('bugs.validation.imageSize'));
      event.target.value = '';
      return;
    }
    setImage(selected);
    setImageError('');
    setSubmitError('');
  };

  const removeImage = () => {
    setImage(null);
    setImageError('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    setSuccessMessage('');

    const cleanTitle = title.trim();
    const cleanDescription = description.trim();
    if (cleanTitle.length < 3) {
      setSubmitError(t('bugs.validation.title'));
      return;
    }
    if (cleanDescription.length < 10) {
      setSubmitError(t('bugs.validation.description'));
      return;
    }

    setSubmitting(true);
    try {
      let attachment;
      if (image) {
        const authorization = await createBugAttachmentUpload({
          file_name: image.name,
          content_type: image.type as BugImageContentType,
          size_bytes: image.size,
        });
        await uploadBugAttachmentFile(authorization, image);
        attachment = authorization.attachment;
      }

      const route = `${location.pathname}${location.search}`.slice(0, 2048) || '/bugs';
      const created = await createBugReport({
        title: cleanTitle,
        description: cleanDescription,
        severity,
        client_context: {
          route,
          page_title: document.title.trim().slice(0, 200) || undefined,
          locale: (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 35),
          user_agent: navigator.userAgent.slice(0, 1024) || 'unknown',
          viewport: {
            width: Math.max(1, Math.min(16384, Math.round(window.innerWidth))),
            height: Math.max(1, Math.min(16384, Math.round(window.innerHeight))),
            device_pixel_ratio: Math.max(0.1, Math.min(10, window.devicePixelRatio || 1)),
          },
        },
        attachment,
      });

      setReports((current) => mergeNewest(current, [created]));
      setTitle('');
      setDescription('');
      setSeverity('medium');
      setImage(null);
      setImageError('');
      setSuccessMessage(t('bugs.success'));
    } catch (error) {
      setSubmitError(errorDetail(error) || t('bugs.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setListError('');
    try {
      const page = await listBugReports(PAGE_SIZE, nextCursor);
      setReports((current) => mergeNewest(current, page.items));
      setNextCursor(page.next_cursor ?? null);
    } catch (error) {
      setListError(errorDetail(error) || t('bugs.errors.load'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDelete = async (report: BugReport) => {
    if (!canDelete || deletingId || !window.confirm(t('bugs.deleteConfirm', { title: report.title }))) {
      return;
    }
    setDeletingId(report.id);
    setListError('');
    try {
      await deleteBugReport(report.id);
      setReports((current) => current.filter((item) => item.id !== report.id));
    } catch (error) {
      setListError(errorDetail(error) || t('bugs.errors.delete'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenAttachment = async (report: BugReport) => {
    if (!report.attachment || openingAttachmentId) return;
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setOpeningAttachmentId(report.id);
    setListError('');
    try {
      const refreshed = await getBugReport(report.id);
      setReports((current) => mergeNewest(current, [refreshed]));
      const readUrl = refreshed.attachment?.read_url;
      if (!readUrl) throw new Error('Attachment is unavailable');
      if (tab) {
        tab.location.href = readUrl;
      } else {
        window.open(readUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      tab?.close();
      setListError(errorDetail(error) || t('bugs.errors.load'));
    } finally {
      setOpeningAttachmentId(null);
    }
  };

  const dateLocale = i18n.resolvedLanguage || i18n.language;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="bugs-page">
      <header>
        <div className="flex items-center gap-3">
          <Bug className="h-7 w-7 text-primary-600" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-gray-900">{t('bugs.title')}</h1>
        </div>
        <p className="mt-1 text-sm text-gray-600">{t('bugs.subtitle')}</p>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]">
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">{t('bugs.form.title')}</h2>
          <form className="mt-5 space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="bug-title" className="mb-1 block text-sm font-medium text-gray-700">
                {t('bugs.form.summary')}
              </label>
              <input
                id="bug-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                minLength={3}
                maxLength={200}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-start focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                placeholder={t('bugs.form.summaryPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="bug-description" className="mb-1 block text-sm font-medium text-gray-700">
                {t('bugs.form.description')}
              </label>
              <textarea
                id="bug-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                minLength={10}
                maxLength={5000}
                required
                rows={6}
                className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-start focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                placeholder={t('bugs.form.descriptionPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">{description.length}/5000</p>
            </div>

            <div>
              <label htmlFor="bug-severity" className="mb-1 block text-sm font-medium text-gray-700">
                {t('bugs.form.severity')}
              </label>
              <select
                id="bug-severity"
                value={severity}
                onChange={(event) => setSeverity(event.target.value as BugSeverity)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                {(['low', 'medium', 'high', 'critical'] as BugSeverity[]).map((value) => (
                  <option key={value} value={value}>{t(`bugs.severity.${value}`)}</option>
                ))}
              </select>
            </div>

            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700">
                {t('bugs.form.image')} <span className="font-normal text-gray-500">({t('common.optional')})</span>
              </span>
              {image && imagePreviewUrl ? (
                <div className="rounded-lg border border-gray-200 p-3">
                  <img
                    src={imagePreviewUrl}
                    alt={t('bugs.form.previewAlt')}
                    className="max-h-52 w-full rounded-md object-contain"
                  />
                  <div className="mt-2 flex min-w-0 items-center justify-between gap-3">
                    <span className="truncate text-sm text-gray-600">{image.name}</span>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                      {t('bugs.form.removeImage')}
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-center hover:border-primary-400 hover:bg-primary-50">
                  <ImagePlus className="h-7 w-7 text-gray-400" aria-hidden="true" />
                  <span className="mt-2 text-sm font-medium text-gray-700">{t('bugs.form.chooseImage')}</span>
                  <span className="mt-1 text-xs text-gray-500">{t('bugs.form.imageHint')}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageChange}
                    className="sr-only"
                    aria-label={t('bugs.form.chooseImage')}
                  />
                </label>
              )}
              {imageError && <p className="mt-2 text-sm text-red-600" role="alert">{imageError}</p>}
            </div>

            {submitError && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{submitError}</span>
              </div>
            )}
            {successMessage && (
              <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700" role="status">
                {successMessage}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitting ? t('bugs.form.submitting') : t('bugs.form.submit')}
            </button>
          </form>
        </section>

        <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('bugs.list.title')}</h2>
            <button
              type="button"
              onClick={() => void loadFirstPage()}
              disabled={loading}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              {t('common.refresh')}
            </button>
          </div>

          {listError && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{listError}</span>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-14">
              <Loader2 className="h-7 w-7 animate-spin text-primary-600" aria-label={t('common.loading')} />
            </div>
          ) : reports.length === 0 ? (
            <p className="py-14 text-center text-sm text-gray-500">{t('bugs.list.empty')}</p>
          ) : (
            <div className="mt-4 space-y-4">
              {reports.map((report) => (
                <article key={report.id} className="rounded-lg border border-gray-200 p-4" data-testid="bug-report">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="break-words font-semibold text-gray-900">{report.title}</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        {t('bugs.list.reportedBy', { name: report.reporter.name })}
                        {' · '}
                        <time dateTime={report.created_at}>
                          {new Intl.DateTimeFormat(dateLocale, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }).format(new Date(report.created_at))}
                        </time>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${severityClasses[report.severity]}`}>
                        {t(`bugs.severity.${report.severity}`)}
                      </span>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => void handleDelete(report)}
                          disabled={deletingId !== null}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                          aria-label={t('bugs.list.deleteLabel', { title: report.title })}
                        >
                          {deletingId === report.id
                            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700">{report.description}</p>

                  {report.attachment && (
                    <button
                      type="button"
                      onClick={() => void handleOpenAttachment(report)}
                      disabled={openingAttachmentId !== null}
                      className="mt-3 block w-fit"
                    >
                      <img
                        src={report.attachment.read_url}
                        alt={t('bugs.list.attachmentAlt', { title: report.title })}
                        className="max-h-44 max-w-full rounded-lg border border-gray-200 object-contain"
                        loading="lazy"
                      />
                      <span className="mt-1 block text-xs font-medium text-primary-600 hover:underline">
                        {openingAttachmentId === report.id
                          ? t('common.loading')
                          : t('bugs.list.openImage')}
                      </span>
                    </button>
                  )}

                  <details className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    <summary className="cursor-pointer font-medium text-gray-700">{t('bugs.list.context')}</summary>
                    <dl className="mt-2 grid gap-1 sm:grid-cols-[max-content_minmax(0,1fr)] sm:gap-x-3">
                      <dt className="font-medium">{t('bugs.list.route')}</dt>
                      <dd className="break-all">{report.client_context.route}</dd>
                      {report.client_context.page_title && (
                        <>
                          <dt className="font-medium">{t('bugs.list.pageTitle')}</dt>
                          <dd className="break-words">{report.client_context.page_title}</dd>
                        </>
                      )}
                      <dt className="font-medium">{t('bugs.list.locale')}</dt>
                      <dd>{report.client_context.locale}</dd>
                      {report.client_context.viewport && (
                        <>
                          <dt className="font-medium">{t('bugs.list.viewport')}</dt>
                          <dd>
                            {report.client_context.viewport.width} × {report.client_context.viewport.height}
                            {report.client_context.viewport.device_pixel_ratio
                              ? ` @ ${report.client_context.viewport.device_pixel_ratio}x`
                              : ''}
                          </dd>
                        </>
                      )}
                      <dt className="font-medium">{t('bugs.list.userAgent')}</dt>
                      <dd className="break-all">{report.client_context.user_agent}</dd>
                    </dl>
                  </details>
                </article>
              ))}
            </div>
          )}

          {nextCursor && !loading && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {loadingMore ? t('bugs.list.loadingMore') : t('bugs.list.loadMore')}
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
